/**
 * A grade do calendário: 7 colunas (as "semanas" da season), uma linha de CSS grid
 * por semana, e os acontecimentos como barras que atravessam as células — o mesmo
 * vocabulário de posicionamento das barras do Roadmap, em dias.
 *
 * <p>Um acontecimento que cruza a virada da semana é fatiado em segmentos, um por
 * linha, com as pontas cortadas sinalizadas. Quando dois se sobrepõem nos mesmos
 * dias, uma varredura gulosa os empilha em faixas — cada semana cresce na altura
 * do que tem, sem empurrar as outras.
 */
import type { Acontecimento } from './calendarioTipos';
// A geometria (fatiar por semana, empilhar em faixas) vive em módulo próprio, puro
// e coberto por teste — este arquivo só desenha o que ela devolve.
import { colunaDo, montarSemanas } from './geometriaDaGrade';

const COR_PADRAO = 'var(--gold)';

export function GradeDaSeason({
  diasTotal,
  acontecimentos,
  aoCriarNoDia,
  aoAbrir,
}: {
  diasTotal: number;
  acontecimentos: Acontecimento[];
  aoCriarNoDia: (dia: number) => void;
  aoAbrir: (acontecimento: Acontecimento) => void;
}) {
  const semanas = montarSemanas(diasTotal, acontecimentos);

  return (
    <div className="cal-grade">
      {semanas.map((segmentos, w) => {
        const faixas = Math.max(1, ...segmentos.map((s) => s.faixa + 1));
        const primeiroDia = w * 7 + 1;
        return (
          <div
            key={w}
            className="cal-semana"
            style={{ '--n-faixas': faixas } as React.CSSProperties}
          >
            {/* Fundos das células: cobrem a coluna inteira e recebem o clique de criação. */}
            {Array.from({ length: 7 }, (_, i) => {
              const dia = primeiroDia + i;
              const existe = dia <= diasTotal;
              return (
                <button
                  key={dia}
                  type="button"
                  className={`cal-dia${existe ? '' : ' fora'}`}
                  style={{ gridColumn: i + 1, gridRow: '1 / -1' }}
                  disabled={!existe}
                  title={existe ? `Criar acontecimento no dia ${dia}` : undefined}
                  onClick={() => aoCriarNoDia(dia)}
                />
              );
            })}

            {/*
              A régua das datas é uma camada própria, na linha 1 da grade — onde barra
              nenhuma entra (elas começam na linha 3). O número nunca divide caixa com
              um acontecimento.
            */}
            {Array.from({ length: 7 }, (_, i) => {
              const dia = primeiroDia + i;
              if (dia > diasTotal) return null;
              return (
                <span
                  key={`data-${dia}`}
                  className="cal-data"
                  style={{ gridColumn: i + 1, gridRow: 1 }}
                >
                  {dia}
                </span>
              );
            })}

            {segmentos.map((segmento) => {
              const { acontecimento } = segmento;
              const umDia = acontecimento.diaInicio === acontecimento.diaFim;
              return (
                <button
                  key={`${acontecimento.id}-${segmento.inicio}`}
                  type="button"
                  className={[
                    'cal-barra',
                    // Classe própria, e não "chip": .chip é dos filtros do Roadmap, e
                    // o CSS é global — a colisão vestia o item de 1 dia de pílula.
                    umDia ? 'cal-um-dia' : '',
                    segmento.continuaEsq ? 'corta-esq' : '',
                    segmento.continuaDir ? 'corta-dir' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    gridColumn: `${colunaDo(segmento.inicio)} / ${colunaDo(segmento.fim) + 1}`,
                    // Linha 1 é a régua do número, linha 2 é um respiro estrutural:
                    // a barra nunca encosta na data, por mais faixas que a semana tenha.
                    gridRow: 3 + segmento.faixa,
                    '--c': acontecimento.cor || COR_PADRAO,
                  } as React.CSSProperties}
                  title={`${acontecimento.nome} · dia ${acontecimento.diaInicio}${
                    umDia ? '' : `–${acontecimento.diaFim}`
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    aoAbrir(acontecimento);
                  }}
                >
                  <span className="cal-barra-nome">{acontecimento.nome}</span>
                  {acontecimento.semVinculo ? null : <span className="cal-barra-elo">⚭</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
